import { motion } from "framer-motion";
import {
  MonitorSmartphone,
  Vote,
  ShieldCheck,
  Camera,
  Headset,
  FileText,
  Home,
  Calculator,
  MessageCircle,
  Send,
  Users,
  Building2,
  UsersRound,
  UserCog,
  Wrench,
  ClipboardCheck,
  NotebookPen,
  Wallet,
  Scale,
  FileSignature,
  TrendingUp,
  BarChart3,
  Bell,
  CalendarDays,
  Megaphone,
  Mail,
  LayoutDashboard,
  Building,
} from "lucide-react";
import { DemoPlayer } from "@/components/DemoPlayer";

const EASE = [0.16, 1, 0.3, 1];

const Card = ({ children, className = "", testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, ease: EASE }}
    whileHover={{ y: -5 }}
    className={`rounded-3xl border border-black/5 bg-white shadow-[0_20px_55px_-30px_rgba(45,52,54,0.3)] transition-shadow duration-300 hover:shadow-[0_30px_70px_-30px_rgba(0,190,200,0.5)] ${className}`}
    data-testid={testid}
  >
    {children}
  </motion.div>
);

const Chip = ({ children }) => (
  <span className="rounded-full border border-white/25 bg-white/15 px-3.5 py-1.5 text-xs font-semibold text-white">
    {children}
  </span>
);

const Label = ({ children }) => (
  <span className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-electric">
    <span className="h-px w-10 bg-electric" />
    {children}
  </span>
);

const Accent = ({ children }) => (
  <span className="bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] bg-clip-text text-transparent">
    {children}
  </span>
);

const Avatar = ({ initials, color }) => (
  <span
    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-ink ${color}`}
  >
    {initials}
  </span>
);

export const Features = () => (
  <section id="fonctionnalites" className="bg-mist py-24 lg:py-32" data-testid="features-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="max-w-3xl"
      >
        <Label>Fonctionnalités</Label>
        <h2 className="mt-6 text-4xl font-bold tracking-normal leading-[1.15] text-ink md:text-5xl lg:text-6xl">
          L'outil dédié au pro,{" "}
          <Accent>sans concession.</Accent>
        </h2>
        <p className="mt-5 text-base leading-relaxed text-ink/60 md:text-lg">
          Contrairement aux logiciels métier classiques, Syndicappli réunit
          communication, assemblées générales et suivi du quotidien dans une
          seule application, pensée avec le plus grand soin.
        </p>
      </motion.div>

      <div className="mt-14 grid gap-5 md:grid-cols-6">
        {/* AG en visio — carte dégradé signature */}
        <Card testid="feature-card-ag" className="relative overflow-hidden border-0 bg-[linear-gradient(135deg,#2BC97E_0%,#0BC1D6_60%,#00C4CC_100%)] md:col-span-4 md:row-span-2">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
          <div className="relative flex h-full flex-col gap-6 p-5 md:p-6">
            <DemoPlayer />
            <div className="px-1 pb-1">
              <div className="flex flex-wrap gap-2">
                <Chip>AG hybride en visioconférence intégrale à 360°</Chip>
                <Chip>Participation AG mode hybride</Chip>
                <Chip>Votes par correspondance digitalisés</Chip>
                <Chip>Processus intégralement digitalisé et sécurisé de bout-en-bout</Chip>
                <Chip>AG en Audioconférence</Chip>
                <Chip>Feuille de présence digitalisée</Chip>
                <Chip>Procès-verbal digitalisé</Chip>
                <Chip>Convocations &amp; PV en LRE</Chip>
              </div>
              <h3 className="mt-5 text-2xl font-bold text-white md:text-3xl">
                AG en Visioconférence à 360°
              </h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-white/85 md:text-base">
                Tenez vos assemblées générales à distance ou en format hybride,
                présentiel et distanciel, avec vote en ligne sécurisé et
                génération automatique du procès-verbal. Démo animée du parcours
                complet ci-dessus.
              </p>
            </div>
          </div>
        </Card>

        {/* Messagerie augmentée — maquette chat */}
        <Card testid="feature-card-messagerie" className="p-7 md:col-span-2">
          <div className="space-y-2.5">
            <div className="flex items-start gap-2">
              <Avatar initials="MC" color="bg-[#F5E960]" />
              <p className="max-w-[200px] rounded-2xl rounded-tl-md bg-[#F1F2F4] px-3 py-1.5 text-xs text-ink">
                Le toit de la terrasse fuit.
              </p>
            </div>
            <div className="flex items-start justify-end gap-2">
              <p className="max-w-[200px] rounded-2xl rounded-tr-md bg-bubble px-3 py-1.5 text-xs text-ink">
                Photo reçue, intervention programmée.
              </p>
              <Avatar initials="JD" color="bg-[#BFE3F7]" />
            </div>
            <div className="flex items-center gap-2">
              <Avatar initials="FD" color="bg-[#D9C9F5]" />
              <p className="w-fit rounded-2xl rounded-tl-md bg-[#F1F2F4] px-3 py-1.5 text-xs text-ink">
                Parfait, merci !
              </p>
            </div>
          </div>
          <h3 className="mt-6 text-xl font-bold text-ink">Messagerie augmentée</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink/60">
            Messagerie instantanée de la Copropriété : gardez le fil des
            échanges entre occupants, gestionnaires et prestataires, comme sur
            un réseau social privé.
          </p>
        </Card>

        {/* App mobile — maquette téléphone */}
        <Card testid="feature-card-mobile" className="p-7 text-center md:col-span-2">
          <div className="mx-auto w-36 rounded-[1.7rem] border border-black/10 bg-white p-1.5 shadow-[0_18px_40px_-18px_rgba(45,52,54,0.4)]">
            <div className="flex h-52 flex-col items-center justify-center gap-3 rounded-[1.35rem] bg-[linear-gradient(160deg,#2BC97E,#00C4CC)]">
              <img src="/logo-white.png" alt="Application mobile Syndicappli" className="h-7 w-auto" />
              <div className="h-1.5 w-16 rounded-full bg-white/40" />
              <div className="h-1.5 w-10 rounded-full bg-white/25" />
            </div>
          </div>
          <h3 className="mt-5 flex items-center justify-center gap-2 text-xl font-bold text-ink">
            <MonitorSmartphone size={20} className="text-electric" />
            App mobile &amp; webapp
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-ink/60">
            Téléchargement gratuit sur iOS et Android, webapp incluse sur
            toute plateforme : smartphone, PC, Mac, tablette.
          </p>
        </Card>

        {/* Comptabilité — maquette budget temps réel */}
        <Card testid="feature-card-comptabilite" className="p-7 md:col-span-2">
          <div className="flex items-start justify-between gap-3">
            <Calculator size={22} className="text-electric" />
            <span className="rounded-full bg-electric/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-electric">
              En option — sur devis
            </span>
          </div>
          <h3 className="mt-3 text-xl font-bold text-ink">Comptabilité en temps réel</h3>
          <div className="mt-4 rounded-2xl border border-black/5 bg-mist p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-ink/40">
              Solde de la copropriété
            </p>
            <p className="mt-1 text-2xl font-bold text-ink">12 480 €</p>
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs font-semibold text-ink/60">
                <span>Budget annuel</span>
                <span>72 %</span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-white">
                <div className="h-full w-[72%] rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)]" />
              </div>
            </div>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-ink/60">
            <li className="flex gap-2">
              <Wallet size={16} className="mt-0.5 shrink-0 text-electric" />
              Comptes, charges et budgets suivis en direct
            </li>
            <li className="flex gap-2">
              <ClipboardCheck size={16} className="mt-0.5 shrink-0 text-electric" />
              Dépenses approuvées en ligne, export en un clic
            </li>
          </ul>
        </Card>

        {/* Vote électronique — maquette sondage */}
        <Card testid="feature-card-vote" className="p-7 md:col-span-2">
          <Vote size={22} className="text-electric" />
          <h3 className="mt-3 text-xl font-bold text-ink">Vote électronique sécurisé</h3>
          <div className="mt-4 space-y-3">
            {[
              { label: "Pour", width: "68%", bar: "bg-[linear-gradient(100deg,#00C6CC,#2FCB74)]" },
              { label: "Contre", width: "22%", bar: "bg-ink/15" },
              { label: "Abstention", width: "10%", bar: "bg-gold/50" },
            ].map((r) => (
              <div key={r.label}>
                <div className="flex items-center justify-between text-xs font-semibold text-ink/60">
                  <span>{r.label}</span>
                  <span>{r.width}</span>
                </div>
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-mist">
                  <div className={`h-full rounded-full ${r.bar}`} style={{ width: r.width }} />
                </div>
              </div>
            ))}
          </div>
          <ul className="mt-4 space-y-2 text-sm text-ink/60">
            <li className="flex gap-2">
              <ShieldCheck size={16} className="mt-0.5 shrink-0 text-electric" />
              Vote à distance et sécurisé
            </li>
            <li className="flex gap-2">
              <ShieldCheck size={16} className="mt-0.5 shrink-0 text-electric" />
              Vote par correspondance digitalisé
            </li>
          </ul>
        </Card>

        {/* Incidents — maquette fidèle à l'app */}
        <Card testid="feature-card-incidents" className="p-7 md:col-span-2">
          <Camera size={22} className="text-electric" />
          <h3 className="mt-3 text-xl font-bold text-ink">Déclaration d'incidents</h3>
          <div className="mt-4 rounded-2xl border border-black/5 bg-mist p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gold">
              12 Février 2020
            </p>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-gold" />
              <p className="font-semibold text-ink">Coupure de courant</p>
            </div>
            <p className="mt-1 text-xs text-ink/50">
              Statut : En cours — un technicien a été prévenu.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Avatar initials="JD" color="bg-[#BFE3F7]" />
              <span className="text-xs font-semibold text-ink/70">John Doe</span>
            </div>
          </div>
        </Card>

        {/* Support — bandeau maquette chatbot */}
        <Card testid="feature-card-support" className="md:col-span-6">
          <div className="grid md:grid-cols-2">
            <div className="flex flex-col justify-center gap-3 bg-[linear-gradient(135deg,#2BC97E,#00C4CC)] p-8 md:p-12">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20 text-white">
                  <Headset size={17} />
                </span>
                <p className="w-fit rounded-2xl rounded-tl-md bg-white px-4 py-2.5 text-sm text-ink shadow-sm">
                  Bonjour ! Comment puis-je vous aider ?
                </p>
              </div>
              <div className="flex items-start justify-end gap-3">
                <p className="w-fit rounded-2xl rounded-tr-md bg-white/25 px-4 py-2.5 text-sm text-white">
                  Je veux organiser mon AG en visioconférence.
                </p>
                <Avatar initials="JD" color="bg-white" />
              </div>
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20 text-white">
                  <Headset size={17} />
                </span>
                <p className="w-fit rounded-2xl rounded-tl-md bg-white px-4 py-2.5 text-sm text-ink shadow-sm">
                  C'est noté, je vous guide étape par étape.
                </p>
              </div>
            </div>
            <div className="p-8 md:p-12">
              <Headset size={22} className="text-electric" />
              <h3 className="mt-3 text-xl font-bold text-ink md:text-2xl">
                Un support humain, un chatbot 24h/24
              </h3>
              <ul className="mt-5 grid gap-3 text-sm text-ink/70 sm:grid-cols-2">
                {[
                  "Support client avec numéro dédié",
                  "Assistance en ligne par chatbot 24h/24 et 7j/7",
                  "Guide d'installation inclus",
                  "Mise à jour gratuite et maintenance incluses",
                ].map((f) => (
                  <li key={f} className="flex gap-2.5">
                    <FileText size={16} className="mt-0.5 shrink-0 text-electric" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {/* Tous les modules — liste intégrale de la barre latérale de l'app */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mt-20 max-w-3xl"
      >
        <Label>Tous les modules</Label>
        <h3 className="mt-5 text-3xl font-bold tracking-normal leading-[1.15] text-ink md:text-4xl">
          L'application dans sa <Accent>totalité.</Accent>
        </h3>
        <p className="mt-3 text-base leading-relaxed text-ink/60">
          Toutes les rubriques de la barre latérale de Syndicappli : navigation
          principale, modules de gestion, services et administration.
        </p>
      </motion.div>

      <div className="mt-10">
        {GROUPS.map((group) => (
          <div
            key={group.id}
            className="mt-10 first:mt-0"
            data-testid={`module-group-${group.id}`}
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-electric">
              {group.name}
            </p>
            <div className={`mt-4 grid gap-3 ${group.cols}`}>
              {group.items.map((item) => (
                <ModuleItem key={item.id} icon={item.icon} label={item.label} id={item.id} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ModuleItem = ({ icon: Icon, label, id }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.5, ease: EASE }}
    whileHover={{ y: -3 }}
    className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white px-4 py-3 shadow-[0_10px_30px_-20px_rgba(45,52,54,0.4)] transition-shadow duration-300 hover:shadow-[0_18px_40px_-20px_rgba(0,190,200,0.55)]"
    data-testid={`feature-module-${id}`}
  >
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-mist text-electric">
      <Icon size={15} />
    </span>
    <span className="text-sm font-semibold text-ink/80">{label}</span>
  </motion.div>
);

const GROUPS = [
  {
    id: "principal",
    name: "Espace principal",
    cols: "sm:grid-cols-2 lg:grid-cols-5",
    items: [
      { icon: Home, label: "Accueil", id: "accueil" },
      { icon: Calculator, label: "Comptabilité", id: "comptabilite" },
      { icon: MessageCircle, label: "Messagerie", id: "messagerie" },
      { icon: Send, label: "Incidents & Sinistres", id: "incidents-sinistres" },
      { icon: Users, label: "Assemblées générales", id: "assemblees-generales" },
    ],
  },
  {
    id: "gestion",
    name: "Gestion",
    cols: "sm:grid-cols-2 lg:grid-cols-3",
    items: [
      { icon: Building2, label: "Mes Immeubles", id: "mes-immeubles" },
      { icon: UsersRound, label: "Mes Copropriétaires", id: "mes-coproprietaires" },
      { icon: UserCog, label: "Mon Équipe", id: "mon-equipe" },
      { icon: Wrench, label: "Mes Prestataires", id: "mes-prestataires" },
      { icon: ClipboardCheck, label: "Visites d'immeuble", id: "visites-immeuble" },
      { icon: NotebookPen, label: "Carnet d'entretien", id: "carnet-entretien" },
      { icon: Calculator, label: "Comptabilité", id: "gestion-comptabilite" },
      { icon: Wallet, label: "Approbation Dépenses", id: "approbation-depenses" },
      { icon: Scale, label: "Documents Juridiques", id: "documents-juridiques" },
      { icon: FileSignature, label: "Contrats fournisseurs", id: "contrats-fournisseurs" },
      { icon: Send, label: "Incidents & Sinistres", id: "gestion-incidents-sinistres" },
      { icon: TrendingUp, label: "Dashboard Prédictif", id: "dashboard-predictif" },
      { icon: ShieldCheck, label: "RGPD & Archivage", id: "rgpd-archivage" },
      { icon: BarChart3, label: "Statistiques", id: "statistiques" },
    ],
  },
  {
    id: "services",
    name: "Services",
    cols: "sm:grid-cols-2 lg:grid-cols-5",
    items: [
      { icon: Bell, label: "Notifications", id: "notifications" },
      { icon: CalendarDays, label: "Événements", id: "evenements" },
      { icon: Megaphone, label: "Annonces", id: "annonces" },
      { icon: Vote, label: "Sondages", id: "sondages" },
      { icon: Mail, label: "Newsletters", id: "newsletters" },
    ],
  },
  {
    id: "administration",
    name: "Administration",
    cols: "sm:grid-cols-2 lg:grid-cols-5",
    items: [
      { icon: LayoutDashboard, label: "Dashboard", id: "admin-dashboard" },
      { icon: Building, label: "Cabinets", id: "cabinets" },
    ],
  },
];
